import { FormControlledInput } from "ui/molecules/form/FormControlledInput";
import { View } from "ui/atoms/view";
import { Controller } from "react-hook-form";
import KeyValueSelector from "ui/organisms/KeyValueSelector";
import { FormControlledInputTextArea } from "ui/molecules/form/FormControlledInputTextArea";
import { useHandleFluidOutput } from "../hooks/useHandleFluidOutput";
import {
  OutputTypeEnums,
  outputTypeOptions,
} from "../../../services/api/types/forms/enums/OutputTypeEnums";
import { requiredValidation } from "ui/utils/Validators";
import { Button, ButtonText } from "ui/gluestack/button";
import { Text } from "react-native";
import { Link, usePathname } from "expo-router";
import { FormControlledDatePicker } from "ui/molecules/form/FormControlledDatePicker";
import { FluidOutputFormFields } from "../../../services/api/types/forms/requests/FluidOutputFormRequest";
import { FormControlledTimePicker } from "ui/molecules/form/FormControlledTimePicker";

interface Props {
  onClose: () => void;
}

export default function CreateFluidOutputForm({ onClose }: Props) {
  const { control, submit, isSubmitting, watch } = useHandleFluidOutput({
    onSuccess: onClose,
  });

  const pathname = usePathname();
  const basePath = pathname.split("/").slice(0, -2).join("/");
  const bowelMovementLink = `${basePath}/bowel-movement`;

  const outputType = watch("output_type");

  return (
    <View>
      <View className="border border-gray-200 w-full mt-6 mb-12" />
      <View className="grid md:grid-cols-2 gap-8">
        <FormControlledDatePicker<FluidOutputFormFields>
          id="recorded_date"
          name="recorded_date"
          control={control}
          label="Date of Output"
          placeholder="00/00/0000"
          isRequired
          isDisabled={isSubmitting}
        />
        <FormControlledTimePicker<FluidOutputFormFields>
          id="recorded_time"
          name="recorded_time"
          control={control}
          label="Time of Output"
          placeholder="00:00"
          isRequired
          isDisabled={isSubmitting}
        />
      </View>
      <View className="w-fit my-9">
        <FormControlledInput
          id="output_volume"
          name="output_volume"
          control={control}
          label="Fluid Output Volume (ml)"
          placeholder="Enter fluid volume"
          isRequired
        ></FormControlledInput>
      </View>
      <View className="mb-6">
        <Controller
          control={control}
          name="output_type"
          render={({ field: { value, onChange }, fieldState: { error } }) => {
            return (
              <KeyValueSelector<OutputTypeEnums>
                title="Output Type"
                options={outputTypeOptions}
                isMultiSelect={false}
                selectedValues={value ? [value] : []}
                onChange={(newValue) => onChange(newValue[0])}
                errorMessage={error}
                checkboxesInSameRow={true}
              />
            );
          }}
          rules={{ required: requiredValidation("Output Type") }}
        />
      </View>
      <View>
        <FormControlledInputTextArea
          id="output_type_description"
          name="output_type_description"
          control={control}
          placeholder="Enter a note, if other please input details here"
          className="text-gray-500 "
          rules={{
            validate: {
              requiredIf: (value) => {
                if (outputType === OutputTypeEnums.OTHER && !value) {
                  return "Description is required for 'Other' type";
                }
                return true;
              },
            },
          }}
        ></FormControlledInputTextArea>
      </View>
      <View className="flex-row gap-x-2 items-center mt-2">
        <Text className="text-sm">
          Please go to the bowel section to fill in data on bowel movements:
        </Text>
        <Link href={bowelMovementLink}>
          <Text className="text-primary-500 font-semibold">Bowel Movement</Text>
        </Link>
      </View>
      <View className="col-span-full">
        <View className="flex flex-row justify-end gap-x-4 mt-10">
          <Button
            action="primary"
            size="sm"
            variant="outline"
            onPress={onClose}
          >
            <ButtonText>Cancel</ButtonText>
          </Button>
          <Button
            action="primary"
            size="sm"
            onPress={submit}
            disabled={isSubmitting}
          >
            <ButtonText className="text-white">Log Output</ButtonText>
          </Button>
        </View>
      </View>
    </View>
  );
}
